import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import propTypes from 'prop-types';
import styled from 'styled-components';
import Copy from './copy.js';
import { Icon, InteractiveIcon } from './icon.js';
import Link from './link.js';
import CDNIllustration from './cdn-illustration.js';
const styles = {
    missingInfoAction: {
        primaryColor: 'mermaidGreen500',
        secondaryColor: 'mermaidGreen100',
        defaultIcon: 'system/search',
        defultActionIcon: 'actions/carrot-right',
    },
    improveAction: {
        primaryColor: 'bubbleBlue500',
        secondaryColor: 'bubbleBlue100',
        defaultIcon: 'actions/circle-info',
        defultActionIcon: 'actions/carrot-right',
    },
    positiveAction: {
        primaryColor: 'seaturtleGreen200',
        secondaryColor: 'seaturtleGreen100',
        defaultIcon: 'feedback/thumbs-up',
        defultActionIcon: 'actions/carrot-right',
    },
    neutralAction: {
        primaryColor: 'mermaidGreen400',
        secondaryColor: 'mermaidGreen100',
        defaultIcon: 'actions/circle-info',
        defultActionIcon: 'actions/carrot-right',
    },
    warning: {
        primaryColor: 'flounderYellow200',
        secondaryColor: 'flounderYellow100',
        defaultIcon: 'actions/circle-warning',
        defultActionIcon: 'actions/close',
    },
    error: {
        primaryColor: 'sebastianRed200',
        secondaryColor: 'sebastianRed100',
        defaultIcon: 'actions/circle-warning',
        defultActionIcon: 'actions/close',
    },
};
export const StyledBanner = styled.aside.withConfig({ displayName: "brc-StyledBanner", componentId: "brc-1usx45c" }) `
  box-shadow: 0 10px 11px -8px rgba(0, 0, 0, 0.12);
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  overflow: hidden;
  background-color: ${({ currentStyles, theme }) => theme[currentStyles.secondaryColor]};
  cursor: ${({ hasLabel, onDismiss, hasAction }) => hasAction && !hasLabel && !onDismiss && 'pointer'};

  &::before {
    ${({ currentStyles, shouldHideBorder, theme }) => !shouldHideBorder &&
    `content: '';
    width: 100%;
    height: 8px;
    position: absolute;
    left: 0;
    top: 0;
    background: ${theme[currentStyles.primaryColor]};
    border-radius: 14px 14px 0 0;`}
  }
  @media (${({ theme }) => theme.forLargerThanPhone}) {
    grid-template-rows: auto;
    grid-template-columns: auto 1fr;
    padding-right: ${({ hasLabel }) => !hasLabel && '64px'};
    text-align: left;
  }
`;
export const StyledBannerAsLink = styled(StyledBanner).attrs(() => ({
    as: 'a',
})).withConfig({ displayName: "brc-StyledBannerAsLink", componentId: "brc-1mkmkz6" }) `
  text-decoration: none;
`;
const BannerContainer = ({ currentStyles, action = () => { }, actionHref, hasLabel, children, className, shouldHideBorder, 'data-testid': dataTestId, }) => {
    const sharedProps = {
        className,
        currentStyles,
        hasLabel,
        shouldHideBorder,
        'data-testid': dataTestId,
    };
    if (!hasLabel) {
        return actionHref ? (_jsx(StyledBannerAsLink, Object.assign({}, sharedProps, { href: actionHref }, { children: children }))) : (_jsx(StyledBanner, Object.assign({}, sharedProps, { onClick: action }, { children: children })));
    }
    else
        return _jsx(StyledBanner, Object.assign({}, sharedProps, { children: children }));
};
export const TitleCopy = styled(Copy).withConfig({ displayName: "brc-TitleCopy", componentId: "brc-1ya8pk7" }) `
  @media (${({ theme }) => theme.forLargerThanPhone}) {
    margin-right: 28px; /* need to give space for the (X) */
  }
`;
const IconContainer = styled.div.withConfig({ displayName: "brc-IconContainer", componentId: "brc-699s4m" }) `
  flex: 0 0 24px;
  height: auto;
  @media (${({ theme }) => theme.forLargerThanPhone}) {
    margin-right: 16px;
  }
`;
const Content = styled.div.withConfig({ displayName: "brc-Content", componentId: "brc-v0dbew" }) `
  flex: 1 1 auto;

  & > ${Copy}, & > ${Link} {
    flex: 1 1 100%;
  }
`;
export const ActionIcon = styled(InteractiveIcon).withConfig({ displayName: "brc-ActionIcon", componentId: "brc-1mkfzy4" }) `
  cursor: pointer;
  position: absolute;
  right: 16px;
  top: 16px;
  width: auto;
  height: 24px;
  color: ${({ theme, defaultcolor = theme.neutral500, name }) => (name === 'close' ? theme.neutral500 : defaultcolor)};
`;
const ChildrenWrapper = styled.div.withConfig({ displayName: "brc-ChildrenWrapper", componentId: "brc-txgxh8" }) `
  width: '100%';
`;
const StyledIcon = styled(Icon).withConfig({ displayName: "brc-StyledIcon", componentId: "brc-49n9t0" }) `
  color: ${({ theme, color }) => theme[color]};
`;
export const Banner = ({ action, actionHref, actionLabel, actionIcon, actionTarget = '', children, className, copy, expandedStyles = {}, icon, onDismiss, shouldHideBorder, CDNIllustrationIcon, title, type = 'neutralAction', }) => {
    var _a;
    const hasLabel = !!actionLabel;
    const hasAction = !!action || !!actionHref;
    const currentStyles = (_a = Object.assign(Object.assign({}, styles), expandedStyles)[type]) !== null && _a !== void 0 ? _a : styles.neutralAction;
    const { primaryColor, defaultIcon, defultActionIcon } = currentStyles;
    return (_jsxs(BannerContainer, Object.assign({ currentStyles: currentStyles, action: action, actionHref: actionHref, hasLabel: hasLabel, shouldHideBorder: shouldHideBorder, onDismiss: onDismiss, className: className, "data-testid": `banner:${type}` }, { children: [_jsx(IconContainer, { children: CDNIllustrationIcon ? (_jsx(CDNIllustration, { filename: CDNIllustrationIcon })) : (_jsx(StyledIcon, { name: icon || defaultIcon, color: primaryColor, "data-testid": "banner-icon" })) }), _jsxs(Content, { children: [title && _jsx(TitleCopy, Object.assign({ bold: true }, { children: title })), copy && _jsx(Copy, { children: copy }), !!children && _jsx(ChildrenWrapper, { children: children }), hasLabel && hasAction && (_jsx(Link, Object.assign({ bold: true, href: actionHref, onClick: action, target: actionTarget }, { children: actionLabel })))] }), !onDismiss && !hasLabel && hasAction && (_jsx(ActionIcon, { name: actionIcon || defultActionIcon, defaultcolor: primaryColor, "data-testid": "action-icon" })), onDismiss && _jsx(ActionIcon, { onClick: onDismiss, "data-testid": "banner-dismiss", name: "actions/close" })] })));
};
Banner.propTypes = {
    action: propTypes.func,
    actionHref: propTypes.string,
    actionLabel: propTypes.node,
    type: propTypes.string,
    icon: propTypes.string,
    actionIcon: propTypes.string,
    title: propTypes.node,
    copy: propTypes.node,
    onDismiss: propTypes.func,
    expandedStyles: propTypes.object,
    shouldHideBorder: propTypes.bool,
};
const StyledBannerExp = styled(Banner).withConfig({ displayName: "brc-StyledBannerExp", componentId: "brc-tom6ia" }) ``;
export default StyledBannerExp;
//# sourceMappingURL=banner.js.map